import { useState, useEffect } from "react";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ShieldCheck, Trash2, UserPlus } from "lucide-react";

export default function SchoolAdmins() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/teacher/admins").then(({ data }) => setAdmins(data.admins)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/teacher/admins", form);
      toast.success(`Admin account created for ${form.name}`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove admin access for ${a.name}?`)) return;
    try {
      await API.delete(`/teacher/admins/${a.id}`);
      toast.success("Admin removed");
      load();
    } catch {
      toast.error("Failed to remove admin");
    }
  };

  const field = (k, props) => (
    <Input {...props} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
      className="bg-black/40 border-white/10 text-white" data-testid={`admin-create-${k}`} required />
  );

  return (
    <div className="glass-surface rounded-xl p-6 mb-6" data-testid="school-admins-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 text-[#FFE600]" />
        <h2 className="font-heading text-lg font-bold text-white">School Admins</h2>
      </div>
      <p className="font-body text-xs text-white/40 mb-5">
        Principals and coaches get view-only access to every class. They sign in at <span className="font-mono text-[#FFE600]">{window.location.origin}/admin</span>
      </p>
      <form onSubmit={create} className="grid sm:grid-cols-4 gap-3 mb-5">
        {field("name", { placeholder: "Name" })}
        {field("email", { placeholder: "Email", type: "email" })}
        {field("password", { placeholder: "Password (8+ chars)", type: "password", minLength: 8 })}
        <button type="submit" disabled={busy} data-testid="admin-create-submit"
          className="h-10 rounded-full bg-[#FFE600] text-black font-heading text-xs font-bold tracking-wider flex items-center justify-center gap-1.5 hover:bg-[#FFE600]/85 disabled:opacity-50 transition-colors duration-200">
          <UserPlus className="w-4 h-4" /> ADD ADMIN
        </button>
      </form>
      {admins.length === 0 ? (
        <p className="font-body text-sm text-white/30" data-testid="admins-empty">No admins yet</p>
      ) : (
        <div className="space-y-2">
          {admins.map((a) => (
            <div key={a.id} className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-white/[0.03]" data-testid={`admin-item-${a.id}`}>
              <div>
                <p className="font-body text-sm text-white">{a.name}</p>
                <p className="font-body text-xs text-white/40">{a.email}</p>
              </div>
              <button onClick={() => remove(a)} data-testid={`admin-remove-${a.id}`} className="p-2 rounded-lg text-white/30 hover:text-magenta hover:bg-magenta/10 transition-colors duration-200">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
